/**
 * Pure refraction-through-glass-block physics helpers.
 * Uses standard SI units and supports the pin-method lab practical.
 */

export interface Point2D {
  x: number;
  y: number;
}

export interface RefractionInput {
  incidentAngleDeg: number;
  refractiveIndex: number;
  thicknessMm?: number;
  airRefractiveIndex?: number;
  blockWidthMm?: number;
}

export interface RefractionTrace {
  incidentAngleDeg: number;
  refractedAngleDeg: number;
  transmittedAngleDeg: number;
  refractiveIndex: number;
  lateralDisplacementMm: number;
  blockThicknessMm: number;
  refractedRaySlope: number;
}

export interface PinAlignmentCheck {
  isAligned: boolean;
  maxDeviation: number;
  meanDeviation: number;
}

function assertFinite(name: string, value: number): void {
  if (!Number.isFinite(value)) {
    throw new RangeError(`${name} must be finite.`);
  }
}

export function calculateRefractiveIndex(incidentAngleDeg: number, refractedAngleDeg: number): number {
  assertFinite('incidentAngleDeg', incidentAngleDeg);
  assertFinite('refractedAngleDeg', refractedAngleDeg);
  if (incidentAngleDeg <= 0 || refractedAngleDeg <= 0) {
    throw new RangeError('Angles must be positive and measured in degrees.');
  }

  const i = incidentAngleDeg * (Math.PI / 180);
  const r = refractedAngleDeg * (Math.PI / 180);
  return Math.sin(i) / Math.sin(r);
}

export function criticalAngle(refractiveIndex: number): number {
  assertFinite('refractiveIndex', refractiveIndex);
  if (refractiveIndex <= 1) {
    throw new RangeError('Refractive index must be greater than 1 for total internal reflection.');
  }

  return (Math.asin(1 / refractiveIndex) * 180) / Math.PI;
}

export function lateralDisplacement(
  incidenceAngleDeg: number,
  refractedAngleDeg: number,
  thicknessMm: number,
  refractiveIndex?: number,
): number {
  assertFinite('incidenceAngleDeg', incidenceAngleDeg);
  assertFinite('refractedAngleDeg', refractedAngleDeg);
  assertFinite('thicknessMm', thicknessMm);
  if (thicknessMm <= 0) {
    throw new RangeError('Thickness must be positive.');
  }

  const incident = incidenceAngleDeg * (Math.PI / 180);
  const refracted = refractedAngleDeg * (Math.PI / 180);
  const n = refractiveIndex ?? calculateRefractiveIndex(incidenceAngleDeg, refractedAngleDeg);

  const displacement = thicknessMm * (Math.sin(incident - refracted) / Math.cos(refracted)) * (n / 1);
  return Math.max(0, displacement);
}

export function checkPinAlignment(points: Point2D[], tolerance = 0.12): PinAlignmentCheck {
  if (points.length < 2) {
    throw new RangeError('At least two pins are required to check alignment.');
  }

  const xs = points.map((point) => point.x);
  const ys = points.map((point) => point.y);
  const xMin = Math.min(...xs);
  const xMax = Math.max(...xs);
  const yMin = Math.min(...ys);
  const yMax = Math.max(...ys);

  const slope = (yMax - yMin) / (xMax - xMin || 1);
  const deviations = points.map((point) => {
    const expectedY = slope * (point.x - xMin) + yMin;
    return Math.abs(point.y - expectedY);
  });

  const meanDeviation = deviations.reduce((sum, value) => sum + value, 0) / deviations.length;
  const maxDeviation = Math.max(...deviations);

  return {
    isAligned: maxDeviation <= tolerance && meanDeviation <= tolerance,
    maxDeviation,
    meanDeviation,
  };
}

export function traceRefractionPath(input: RefractionInput): RefractionTrace {
  const incidentAngleDeg = input.incidentAngleDeg;
  const refractiveIndex = input.refractiveIndex;
  const thicknessMm = input.thicknessMm ?? 60;
  const airRefractiveIndex = input.airRefractiveIndex ?? 1;

  assertFinite('incidentAngleDeg', incidentAngleDeg);
  assertFinite('refractiveIndex', refractiveIndex);
  assertFinite('thicknessMm', thicknessMm);
  assertFinite('airRefractiveIndex', airRefractiveIndex);

  if (incidentAngleDeg <= 0 || incidentAngleDeg >= 90) {
    throw new RangeError('Incident angle must be between 0° and 90° exclusive.');
  }
  if (refractiveIndex <= airRefractiveIndex) {
    throw new RangeError('Refractive index must be greater than the incident-medium index.');
  }

  const incidentRadians = (incidentAngleDeg * Math.PI) / 180;
  const refractedAngleDeg = (Math.asin((airRefractiveIndex / refractiveIndex) * Math.sin(incidentRadians)) * 180) / Math.PI;
  const transmittedAngleDeg = (Math.asin((refractiveIndex / airRefractiveIndex) * Math.sin((refractedAngleDeg * Math.PI) / 180)) * 180) / Math.PI;
  const displacement = lateralDisplacement(incidentAngleDeg, refractedAngleDeg, thicknessMm, refractiveIndex);

  return {
    incidentAngleDeg,
    refractedAngleDeg,
    transmittedAngleDeg,
    refractiveIndex,
    lateralDisplacementMm: displacement,
    blockThicknessMm: thicknessMm,
    refractedRaySlope: Math.tan((refractedAngleDeg * Math.PI) / 180),
  };
}
